import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/errors/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { Database } from '@/integrations/supabase/types';

type WorkOrder = Database['public']['Tables']['workbook_services']['Row'];

export interface ServiceGenerationInput {
  problemStatement: string;
  solutionDescription: string;
}

export interface ServiceGenerationResult {
  serviceName: string;
}

export interface UseServiceGenerationReturn {
  generateService: (input: ServiceGenerationInput, workbookId: string) => Promise<void>;
  isGenerating: boolean;
  error: string | null;
}

/**
 * Hook for AI-powered service generation
 * Transforms user problems and solutions into structured service data
 */
export function useServiceGeneration(): UseServiceGenerationReturn {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const generateService = async (input: ServiceGenerationInput, workbookId: string) => {
    setIsGenerating(true);
    setError(null);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      console.log('[useServiceGeneration] Starting service generation:', {
        problemLength: input.problemStatement.length,
        solutionLength: input.solutionDescription.length,
        workbookId
      });

      // Combine problem and solution for the AI prompt
      const combinedPrompt = `Problem: ${input.problemStatement}\nSolution: ${input.solutionDescription}`;

      // Call text generation endpoint with service_generation type
      const { data, error: functionError } = await supabase.functions.invoke('text-generation', {
        body: {
          message: combinedPrompt,
          type: 'service_generation',
          appName: 'Service Creator',
          functionName: 'Service Generation'
        }
      });

      if (functionError) {
        throw new Error(functionError.message || 'Failed to generate service');
      }

      if (!data?.generatedText) {
        throw new Error('No content received from AI generation');
      }

      console.log('[useServiceGeneration] Raw AI response:', data.generatedText);

      // Parse JSON response from AI
      let parsedData: ServiceGenerationResult;
      try {
        // Clean up any potential markdown formatting or extra text
        const cleanJson = data.generatedText
          .replace(/^```json\s*\n?/gm, '')
          .replace(/^```\s*\n?/gm, '')
          .replace(/\n?```$/gm, '')
          .trim();

        parsedData = JSON.parse(cleanJson);

        // Validate required fields
        if (!parsedData.serviceName) {
          throw new Error('Missing service name in AI response');
        }
      } catch (parseError) {
        console.error('[useServiceGeneration] Failed to parse AI response:', parseError);
        throw new Error('Failed to parse AI response. Please try again.');
      }

      console.log('[useServiceGeneration] Parsed service data:', parsedData);

      // Always create a new work order when this function is called
      // This ensures "Create New Work Order" button actually creates a new one
      const { data: newWorkOrder, error: createError } = await supabase
        .from('workbook_services')
        .insert({
          workbook_id: workbookId,
          service_name: parsedData.serviceName, // Use as work order name
          work_order_status: 'draft',
          problem_statement: input.problemStatement,
          solution_description: input.solutionDescription,
          ai_generated_metadata: {
            suggested_name: parsedData.serviceName,
            generated_at: new Date().toISOString()
          },
          added_by: user.id,
          total_amount: 0
        })
        .select()
        .single();

      if (createError || !newWorkOrder) {
        console.error('[useServiceGeneration] Failed to create work order:', createError);
        throw new Error('Failed to create work order.');
      }
      
      console.log('[useServiceGeneration] Work order created successfully:', newWorkOrder.id);

      // Invalidate cart queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['shopping-cart', workbookId] });

      toast({
        title: 'Service Created',
        description: `"${parsedData.serviceName}" has been added to your work order`,
        variant: 'default',
      });

      console.log('[useServiceGeneration] Service created successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      console.error('[useServiceGeneration] Error:', errorMessage);
      setError(errorMessage);
      
      toast({
        title: 'Generation Failed',
        description: errorMessage,
        variant: 'destructive',
      });
      
      throw err;
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    generateService,
    isGenerating,
    error,
  };
}